import type { Block } from '@sarnia/shared';
import { editorStore, useEditorSelector } from './editorStore';
import { Icon } from '../components/ui/Icon';

function BlockLabel(block: Block): string {
  return `${block.type} · ${block.id.slice(0, 8)}`;
}

export default function StructureTree() {
  const site = useEditorSelector((state) => state.site);
  const selectedBlockId = useEditorSelector((state) => state.selectedBlockId);
  const selectBlock = (id: string | null) => editorStore.selectBlock(id);
  const moveBlock = (id: string, afterId?: string) => editorStore.moveBlock(id, afterId);
  const removeBlock = (id: string) => editorStore.removeBlock(id);

  if (!site) {
    return <div className="structure-tree-empty">Loading structure…</div>;
  }

  const blocks = site.blocks;

  return (
    <div className="structure-tree">
      {blocks.map((block, index) => (
        <div
          key={block.id}
          className={`structure-tree-item ${
            selectedBlockId === block.id ? 'structure-tree-item--selected' : ''
          }`}
          draggable
          onDragStart={(event) => {
            event.dataTransfer.setData('text/plain', block.id);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const draggedId = event.dataTransfer.getData('text/plain');
            if (draggedId && draggedId !== block.id) {
              moveBlock(draggedId, block.id);
            }
          }}
          onClick={() => selectBlock(block.id)}
        >
          <span className="structure-tree-drag" title="Drag to reorder">
            <Icon name="move" size={15} />
          </span>
          <span className="structure-tree-label">{BlockLabel(block)}</span>
          <span className="structure-tree-actions">
            <button
              type="button"
              className="structure-tree-order"
              disabled={index === 0}
              onClick={(event) => {
                event.stopPropagation();
                const prevBlock = blocks[index - 1];
                if (prevBlock) {
                  moveBlock(block.id, prevBlock.id);
                }
              }}
              aria-label="Move up"
            >
              ↑
            </button>
            <button
              type="button"
              className="structure-tree-order"
              disabled={index === blocks.length - 1}
              onClick={(event) => {
                event.stopPropagation();
                const nextBlock = blocks[index + 1];
                if (nextBlock) {
                  moveBlock(block.id, nextBlock.id);
                }
              }}
              aria-label="Move down"
            >
              ↓
            </button>
            <button
              type="button"
              className="structure-tree-delete"
              onClick={(event) => {
                event.stopPropagation();
                removeBlock(block.id);
              }}
              aria-label="Delete block"
            >
              Delete
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}
